import { useIsomorphicLayoutEffect } from "motion/react";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { pb, refreshSession } from "@/lib/pocketbase";
import type { DoctorsResponse } from "@/types/pocketbase";

const REFRESH_INTERVAL_MS = 30_000;

type PocketContext = DoctorsResponse | undefined;

const OUTSIDE_PROVIDER = Symbol("outside_provider");
const PocketContext = createContext<PocketContext | typeof OUTSIDE_PROVIDER>(undefined);

type SessionProviderProps = {
	children: ReactNode;
};

export function SessionProvider({ children }: SessionProviderProps) {
	const [session, setSession] = useState<DoctorsResponse | undefined>(
		() => pb.authStore.record as DoctorsResponse | undefined
	);

	useEffect(() => {
		const unsubscribe = pb.authStore.onChange((_, record) => {
			setSession(record as DoctorsResponse | undefined);
		});

		return () => {
			unsubscribe();
		};
	}, []);

	useInterval(() => {
		refreshSession();
	}, REFRESH_INTERVAL_MS);

	return <PocketContext value={session}>{children}</PocketContext>;
}

export const useSession = () => {
	const context = useContext(PocketContext);

	if (context === OUTSIDE_PROVIDER) {
		throw new Error("usePocket must be used within a SessionProvider");
	}

	return context;
};

export function useInterval(callback: () => void, delay: number | undefined) {
	const savedCallback = useRef(callback);

	// Remember the latest callback if it changes.
	useIsomorphicLayoutEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	// Set up the interval.
	useEffect(() => {
		// Don't schedule if no delay is specified.
		// Note: 0 is a valid value for delay.
		if (delay === null || delay === undefined) {
			return;
		}

		const id = setInterval(() => {
			savedCallback.current();
		}, delay);

		return () => {
			clearInterval(id);
		};
	}, [delay]);
}
